import type {
  Assessment as AssessmentDataType,
  Question as QuestionDataType,
  Section as SectionDataType,
} from '@playbooks/core/src/interfaces/ActionCard'

import type { GeneratedPlayResponse } from '@playbooks/core/src/services/BackendClient'

// Matches datetime formats: YYYY-MM-DDTHH:MM:SS, YYYY-MM-DDTHH:MM:SSZ, or YYYY-MM-DDTHH:MM:SS+/-HH:MM.
const DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:Z|[+-]\d{2}:\d{2})?$/

/**
 * Converts an AI datetime answer into the UTC string format stored by Action Cards
 */
function normalizeDateTimeAnswer(answer: GeneratedPlayResponse['answer']): GeneratedPlayResponse['answer'] {
  // Ignore anything outside the strict datetime formats requested from the model
  if (typeof answer !== 'string') return answer
  if (!DATETIME_PATTERN.test(answer)) return answer

  // Modern browsers handle the timezones exactly as we need when parsing:
  // - zone-less ISO datetimes → as local wall-clock time
  // - zoned values → as absolute instants
  const parsedDate = new Date(answer)

  // Store the normalized UTC instant; fall back to the original value if the date is invalid
  return isNaN(parsedDate.getTime()) ? answer : parsedDate.toISOString()
}

/**
 * Fill in the answers from the AI response into the Action Card.
 * @param response Generated response from the AI model
 * @param node Action Card node to update
 * @story User Story 9721: New Action Card Widget: Update with AI
 */
export function applyAiResponse(response: GeneratedPlayResponse, node: any) {
  switch (node.type) {
    case 'Text':
    case 'LongText':
    case 'Amount':
    case 'Number':
    case 'Date':
    case 'Email':
    case 'Phone':
    case 'Percentage':
      ;(node as QuestionDataType).value = '' + response.answer
      break
    case 'DateTime':
      ;(node as QuestionDataType).value = '' + normalizeDateTimeAnswer(response.answer)
      break
    case 'Checkbox':
      ;(node as QuestionDataType).value = response.answer
      break
    case 'Dropdown':
      if (response.answerIds?.length) {
        ;(node as QuestionDataType).value = response.answerIds[0]
      }
      break
    case 'multi select':
      if (response.answerIds?.length && node.responses) {
        const assessment = node as AssessmentDataType
        assessment.responses.forEach(r => {
          if (response.answerIds?.includes(r.id)) {
            r.checked = true
          }
        })
        // Set free text value (e.g. for "Other" option) (#12790)
        assessment.freeTextValue = response.answer ?? undefined
        // The "switch" against the assessment question has to be checked
        // if any responses are checked
        assessment.checked = true
      }
      break
    case 'single select':
      if (response.answerIds?.length) {
        const assessment = node as AssessmentDataType
        const answerId = response.answerIds[0]
        const currentlyCheckedOption = assessment.responses.find(r => r.checked)
        const newCheckedOption = assessment.responses.find(r => r.id === answerId)
        if (newCheckedOption && newCheckedOption.id !== currentlyCheckedOption?.id) {
          // Uncheck the currently checked option
          if (currentlyCheckedOption) {
            currentlyCheckedOption.checked = false
          }
          // Check the new option
          newCheckedOption.checked = true
          // Set free text value (e.g. for "Other" option) (#12790)
          assessment.freeTextValue = response.answer ?? undefined
          // The "switch" against the assessment question has to be checked
          // if any responses are checked
          assessment.checked = true
        } else if (response.answer) {
          // Even if the selected option didn't change, the free text value might have (#12790, #13796)
          assessment.freeTextValue = response.answer
        }
      }
      break
    case 'Notes':
      ;(node as SectionDataType).value = response.answer
      break
    default:
      console.warn(`Unknown node type: ${node.type}`)
  }
}
